import * as accessCallables from "../../app/firebase/callables.js";
/**
 * @file Settings-owned user lifecycle and invite callable helpers.
 */

import {
    checkUserDeactivationImpactCallable as configuredCheckUserDeactivationImpactCallable,
    claimUserInviteCallable as configuredClaimUserInviteCallable,
    createUserInviteCallable as configuredCreateUserInviteCallable,
    deactivateUserWithDraftCallable as configuredDeactivateUserWithDraftCallable,
    deleteUserDeactivationDraftCallable as configuredDeleteUserDeactivationDraftCallable,
    deleteUserInviteCallable as configuredDeleteUserInviteCallable,
    listUserDeactivationDraftsCallable as configuredListUserDeactivationDraftsCallable,
    openUserDeactivationDraftCallable as configuredOpenUserDeactivationDraftCallable,
    reactivateUserCallable as configuredReactivateUserCallable,
    refreshUserDeactivationDraftCallable as configuredRefreshUserDeactivationDraftCallable,
    runUserDeactivationMigrationCallable as configuredRunUserDeactivationMigrationCallable,
    saveUserDeactivationDraftCallable as configuredSaveUserDeactivationDraftCallable,
    sendUserInviteCallable as configuredSendUserInviteCallable,
    updateUserInviteCallable as configuredUpdateUserInviteCallable,
} from "../../app/firebase/callables.js";

/**
 * Creates user lifecycle API helpers.
 * @param {object} deps Dependency bag.
 * @returns {object} User lifecycle API helpers.
 */
export function createUserLifecycleApi({
    checkUserDeactivationImpactCallable,
    claimUserInviteCallable,
    createUserInviteCallable,
    deactivateUserWithDraftCallable,
    deleteUserDeactivationDraftCallable,
    deleteUserInviteCallable,
    listUserDeactivationDraftsCallable,
    openUserDeactivationDraftCallable,
    reactivateUserCallable,
    refreshUserDeactivationDraftCallable,
    runUserDeactivationMigrationCallable,
    saveUserDeactivationDraftCallable,
    sendUserInviteCallable,
    updateUserInviteCallable,
}) {
/**
 * Rejects retryable incomplete lifecycle responses before Settings can publish
 * a successful deactivation/reactivation state.
 * @param {object} result Callable lifecycle result.
 * @param {string} action Human-readable lifecycle action.
 * @returns {object} Confirmed successful result.
 */
function requireCompletedLifecycleResult(result, action) {
    if (result?.success === true) {
        return result;
    }

    const error = new Error(
        result?.rerunRequired
            ? `${action} is incomplete. Resolve the Compliance People conflict and run it again.`
            : `${action} did not complete.`,
    );
    error.code = result?.reason || "lifecycle_incomplete";
    error.lifecycleResult = result || null;
    throw error;
}

/**
 * Checks the active-work and blocker impact of deactivating a user.
 * @param {string} targetUserId - The user being assessed for deactivation.
 * @returns {Promise<object>} Grouped deactivation impact payload.
 */
async function checkUserDeactivationImpact(targetUserId) {
    if (!targetUserId) {
        throw new Error('Missing target user.');
    }

    try {
        const result = await checkUserDeactivationImpactCallable({ targetUserId });
        return result.data;
    } catch (error) {
        console.error('Error checking user deactivation impact:', error);
        throw error;
    }
}

async function listUserDeactivationDrafts() {
    try {
        const result = await listUserDeactivationDraftsCallable();
        return result.data?.drafts || [];
    } catch (error) {
        console.error('Error listing user deactivation drafts:', error);
        throw error;
    }
}

async function openUserDeactivationDraft(targetUserId) {
    if (!targetUserId) {
        throw new Error('Missing target user.');
    }

    try {
        const result = await openUserDeactivationDraftCallable({ targetUserId });
        return result.data;
    } catch (error) {
        console.error('Error opening user deactivation draft:', error);
        throw error;
    }
}

async function saveUserDeactivationDraft(targetUserId, payload = {}) {
    if (!targetUserId) {
        throw new Error('Missing target user.');
    }

    try {
        const result = await (payload.operation === "access_change" ?
            accessCallables.saveUserAccessChangeDraftCallable :
            saveUserDeactivationDraftCallable)({
            targetUserId,
            defaultReplacementUserId: payload.defaultReplacementUserId || null,
            selections: payload.selections || {},
            ...(payload.draftId ? {draftId: payload.draftId} : {}),
        });
        return result.data;
    } catch (error) {
        console.error('Error saving user deactivation draft:', error);
        throw error;
    }
}

async function refreshUserDeactivationDraft(targetUserId, context = {}) {
    if (!targetUserId) {
        throw new Error('Missing target user.');
    }

    try {
        const result = await (context.operation === "access_change" ?
            accessCallables.refreshUserAccessChangeDraftCallable :
            refreshUserDeactivationDraftCallable)({ targetUserId,
                ...(context.draftId ? {draftId: context.draftId} : {}) });
        return result.data;
    } catch (error) {
        console.error('Error refreshing user deactivation draft:', error);
        throw error;
    }
}

async function deleteUserDeactivationDraft(targetUserId, context = {}) {
    if (!targetUserId) {
        throw new Error('Missing target user.');
    }

    try {
        const result = await (context.operation === "access_change" ?
            accessCallables.deleteUserAccessChangeDraftCallable :
            deleteUserDeactivationDraftCallable)({ targetUserId,
                ...(context.draftId ? {draftId: context.draftId} : {}) });
        return result.data;
    } catch (error) {
        console.error('Error deleting user deactivation draft:', error);
        throw error;
    }
}

async function runUserDeactivationMigration(targetUserId, context = {}) {
    if (!targetUserId) {
        throw new Error('Missing target user.');
    }

    try {
        const result = await (context.operation === "access_change" ?
            accessCallables.runUserAccessChangeMigrationCallable :
            runUserDeactivationMigrationCallable)({ targetUserId,
                ...(context.draftId ? {draftId: context.draftId} : {}) });
        return result.data;
    } catch (error) {
        console.error('Error running user deactivation migration:', error);
        throw error;
    }
}

async function deactivateUserWithDraft(targetUserId) {
    if (!targetUserId) {
        throw new Error('Missing target user.');
    }

    try {
        const result = await deactivateUserWithDraftCallable({ targetUserId });
        return requireCompletedLifecycleResult(
            result.data,
            "User deactivation",
        );
    } catch (error) {
        console.error('Error deactivating user with draft:', error);
        throw error;
    }
}

async function reactivateUser(targetUserId) {
    if (!targetUserId) {
        throw new Error('Missing target user.');
    }

    try {
        const result = await reactivateUserCallable({ targetUserId });
        return requireCompletedLifecycleResult(
            result.data,
            "User reactivation",
        );
    } catch (error) {
        console.error('Error reactivating user:', error);
        throw error;
    }
}

async function createUserInvite(inviteData) {
    try {
        const result = await createUserInviteCallable(inviteData);
        return result.data;
    } catch (error) {
        console.error('Error creating user invite:', error);
        throw error;
    }
}

async function updateUserInvite(inviteId, inviteData) {
    if (!inviteId) {
        throw new Error('Missing invite ID.');
    }

    try {
        const result = await updateUserInviteCallable({ inviteId, ...inviteData });
        return result.data;
    } catch (error) {
        console.error('Error updating user invite:', error);
        throw error;
    }
}

async function deleteUserInvite(inviteId) {
    if (!inviteId) {
        throw new Error('Missing invite ID.');
    }

    try {
        const result = await deleteUserInviteCallable({ inviteId });
        return result.data;
    } catch (error) {
        console.error('Error deleting user invite:', error);
        throw error;
    }
}

async function sendUserInvite(inviteId) {
    if (!inviteId) {
        throw new Error('Missing invite ID.');
    }

    try {
        const result = await sendUserInviteCallable({ inviteId });
        return result.data;
    } catch (error) {
        console.error('Error sending user invite:', error);
        throw error;
    }
}

async function claimUserInvite(claimData = {}) {
    try {
        const result = await claimUserInviteCallable(claimData);
        return result.data || { claimed: false, applied: false };
    } catch (error) {
        console.error('Error claiming user invite:', error);
        throw error;
    }
}



    return {
        checkUserDeactivationImpact,
        listUserDeactivationDrafts,
        openUserDeactivationDraft,
        saveUserDeactivationDraft,
        refreshUserDeactivationDraft,
        deleteUserDeactivationDraft,
        runUserDeactivationMigration,
        deactivateUserWithDraft,
        reactivateUser,
        createUserInvite,
        updateUserInvite,
        deleteUserInvite,
        sendUserInvite,
        claimUserInvite,
    };
}

const configuredUserLifecycleApi = createUserLifecycleApi({
    checkUserDeactivationImpactCallable: configuredCheckUserDeactivationImpactCallable,
    claimUserInviteCallable: configuredClaimUserInviteCallable,
    createUserInviteCallable: configuredCreateUserInviteCallable,
    deactivateUserWithDraftCallable: configuredDeactivateUserWithDraftCallable,
    deleteUserDeactivationDraftCallable: configuredDeleteUserDeactivationDraftCallable,
    deleteUserInviteCallable: configuredDeleteUserInviteCallable,
    listUserDeactivationDraftsCallable: configuredListUserDeactivationDraftsCallable,
    openUserDeactivationDraftCallable: configuredOpenUserDeactivationDraftCallable,
    reactivateUserCallable: configuredReactivateUserCallable,
    refreshUserDeactivationDraftCallable: configuredRefreshUserDeactivationDraftCallable,
    runUserDeactivationMigrationCallable: configuredRunUserDeactivationMigrationCallable,
    saveUserDeactivationDraftCallable: configuredSaveUserDeactivationDraftCallable,
    sendUserInviteCallable: configuredSendUserInviteCallable,
    updateUserInviteCallable: configuredUpdateUserInviteCallable,
});

export const checkUserDeactivationImpact =
    configuredUserLifecycleApi.checkUserDeactivationImpact;
export const listUserDeactivationDrafts =
    configuredUserLifecycleApi.listUserDeactivationDrafts;
export const openUserDeactivationDraft =
    configuredUserLifecycleApi.openUserDeactivationDraft;
export const saveUserDeactivationDraft =
    configuredUserLifecycleApi.saveUserDeactivationDraft;
export const refreshUserDeactivationDraft =
    configuredUserLifecycleApi.refreshUserDeactivationDraft;
export const deleteUserDeactivationDraft =
    configuredUserLifecycleApi.deleteUserDeactivationDraft;
export const runUserDeactivationMigration =
    configuredUserLifecycleApi.runUserDeactivationMigration;
export const deactivateUserWithDraft =
    configuredUserLifecycleApi.deactivateUserWithDraft;
export const reactivateUser = configuredUserLifecycleApi.reactivateUser;
export const createUserInvite = configuredUserLifecycleApi.createUserInvite;
export const updateUserInvite = configuredUserLifecycleApi.updateUserInvite;
export const deleteUserInvite = configuredUserLifecycleApi.deleteUserInvite;
export const sendUserInvite = configuredUserLifecycleApi.sendUserInvite;
export const claimUserInvite = configuredUserLifecycleApi.claimUserInvite;
